import classNames from 'classnames';

import { Icon, IconName } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { getDomain, normalizeUrl } from '@/public/utils/url';

import styles from './url-chip.module.scss';

interface UrlChipProps {
  url: string;
  className?: string;
}

const googleDomains = [
  'docs.google.com',
  'sheets.google.com',
  'slides.google.com',
  'drive.google.com',
];

// Maximum characters to show for a URL path segment before truncating
const MAX_SEGMENT_LENGTH = 15;

const getIconForDomain = (domain: string | null): IconName => {
  if (domain === null) {
    return 'Globe';
  }
  if (domain.endsWith('supersimple.io')) {
    return 'Supersimple';
  }
  if (domain.endsWith('slack.com')) {
    return 'Slack';
  }
  if (domain.endsWith('github.com')) {
    return 'GitHub';
  }
  if (domain.endsWith('linear.app')) {
    return 'Linear';
  }
  if (googleDomains.some((googleDomain) => domain.endsWith(googleDomain))) {
    return 'GoogleDrive';
  }
  if (domain.endsWith('notion.so') || domain.endsWith('notion.site')) {
    return 'Notion';
  }
  if (domain.endsWith('hubspot.com')) {
    return 'HubSpot';
  }
  return 'Globe';
};

export const getDisplayText = (url: string): string => {
  const domain = getDomain(url);
  if (domain === null) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // Return just domain if no meaningful path
    if (path === '' || path === '/') {
      return domain;
    }

    const pathSegments = path.split('/').filter((segment) => segment !== '');
    const firstSegment = pathSegments[0];

    if (firstSegment === undefined) {
      return domain;
    }

    const hasMorePath = pathSegments.length > 1;
    const isLongSegment = firstSegment.length > MAX_SEGMENT_LENGTH;

    if (hasMorePath) {
      if (isLongSegment) {
        return `${domain}/${firstSegment.slice(0, MAX_SEGMENT_LENGTH)}...`;
      }
      return `${domain}/${firstSegment}/...`;
    }

    if (isLongSegment) {
      return `${domain}/${firstSegment.slice(0, MAX_SEGMENT_LENGTH)}...`;
    }

    return `${domain}/${firstSegment}`;
  } catch {
    return domain;
  }
};

export const UrlChip = ({ url, className }: UrlChipProps) => {
  const normalizedUrl = normalizeUrl(url);
  const domain = getDomain(normalizedUrl);
  const icon = getIconForDomain(domain);
  const displayText = getDisplayText(normalizedUrl);

  return (
    <Tooltip content={url}>
      <a
        href={normalizedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={classNames(styles.urlChip, className)}>
        <Icon name={icon} size={16} />
        <span className={styles.text}>{displayText}</span>
      </a>
    </Tooltip>
  );
};
